import { forwardRef, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { borderColor, primaryText } from 'styles/theme';
import { v4 } from 'uuid';

export const SectionHeader = styled.div`
    color: ${primaryText};
`;

const Section = styled.section`
    & + & {
        margin-top: 1rem;
        border-top: 1px solid ${borderColor};
    }
    & + &:last-of-type {
        margin-bottom: 1rem;
    }
    padding: 0 1rem;
`;

export type PaneSectionProps = Readonly<{
    /**
     * The section label.
     */
    label?: ReactNode;
    /**
     * The section content.
     */
    children?: ReactNode;
    /**
     * ARIA text label for this element.
     */
    'aria-label'?: string;
    /**
     * Optional class name for the section.
     */
    className?: string;
}>;

const PaneSection = forwardRef<HTMLDivElement, PaneSectionProps>(
    ({ label, children, 'aria-label': ariaLabel, className }, ref) => {
        const [id] = useState(() => `pane-section-header-${v4()}`);
        return (
            <Section
                ref={ref}
                aria-label={ariaLabel}
                className={className}
                aria-labelledby={id}
            >
                {label && (
                    <SectionHeader id={id} className="bold my-10">
                        {label}
                    </SectionHeader>
                )}
                <div data-testid="pane-section-content">{children}</div>
            </Section>
        );
    }
);

/**
 * Component for rendering a section within a `CollapsiblePane` component.
 */
export { PaneSection };
